/**
 * Minimal authenticated RPC transport for the `app` schema through PostgREST.
 * Session ownership stays with the caller (supabase-js on web); this layer
 * only attaches the current access token and maps failures to ApiError.
 * Response bodies are parsed but never logged.
 */
import { ApiError } from './errors.ts';

export interface TransportConfig {
  url: string;
  anonKey: string;
  /** Returns the current access token, refreshing it if the owner can. */
  getAccessToken: () => Promise<string | null>;
  fetch?: typeof fetch;
  timeoutMs?: number;
  /** Executing client recorded in Activity (an untrusted label, e.g. web, desktop, lotshots). */
  client?: { kind: 'web' | 'desktop' | 'lotshots' | 'mobile'; device?: string };
}

export interface Transport {
  rpc<T>(name: string, args: Record<string, unknown>, signal?: AbortSignal): Promise<T>;
}

export function createTransport(config: TransportConfig): Transport {
  const doFetch = config.fetch ?? globalThis.fetch.bind(globalThis);
  const timeoutMs = config.timeoutMs ?? 15000;
  return {
    async rpc<T>(name: string, args: Record<string, unknown>, signal?: AbortSignal): Promise<T> {
      if (!/^[a-z_]+$/.test(name)) throw Error('invalid_rpc_name');
      const token = await config.getAccessToken();
      if (!token) throw new ApiError(401);
      const timeout = AbortSignal.timeout(timeoutMs);
      const combined = signal ? AbortSignal.any([signal, timeout]) : timeout;
      let response: Response;
      try {
        response = await doFetch(`${config.url}/rest/v1/rpc/${name}`, {
          method: 'POST',
          signal: combined,
          headers: {
            apikey: config.anonKey,
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Content-Profile': 'app',
            'Accept-Profile': 'app',
            ...(config.client ? { 'x-miraops-client': config.client.kind, ...(config.client.device ? { 'x-miraops-device': config.client.device.slice(0, 40) } : {}) } : {}),
          },
          body: JSON.stringify(args),
        });
      } catch (error) {
        // A caller abort stays an abort; a timeout or socket failure has no response.
        if (signal?.aborted) throw error;
        throw error instanceof Error ? Object.assign(new Error('network_failure'), { name: 'NetworkError' }) : error;
      }
      const text = await response.text();
      if (!response.ok) {
        let code: string | undefined, token: string | undefined, hint: string | undefined;
        try {
          const body = JSON.parse(text) as { code?: string; message?: string; hint?: string };
          code = body.code;
          // Only short machine tokens raised by our own RPCs are retained.
          token = typeof body.message === 'string' && /^[a-z_]{3,40}$/.test(body.message) ? body.message : undefined;
          hint = typeof body.hint === 'string' ? body.hint : undefined;
        } catch { /* non-JSON gateway error */ }
        throw new ApiError(response.status, code, token, hint);
      }
      return (text ? JSON.parse(text) : null) as T;
    },
  };
}
